import { CommonModule } from "@angular/common";
import { Component, OnInit, signal } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { FUNCTION_RESULT_STATUS } from "@nova/common-interfaces";
import {
  IAsset,
  ICustomerOverview,
  IInvoice,
  IPayments,
  ISubscription,
} from "@rfm/shared-interfaces";
import { BlockUIModule } from "ng-block-ui";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogService } from "primeng/dynamicdialog";
import { MenuModule } from "primeng/menu";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ToastModule } from "primeng/toast";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { BehaviorSubject, Observable } from "rxjs";
import { CommonComponent } from "../../../../../lib/components/index.mjs";
import { JsonToTextPipe } from "../../../../../lib/pipes/json-to-text.pipe.mjs";
import { LatestInvoicePaymentComponent } from "../../../../billing/components/make-payment/latest-invoice-payment/latest-invoice-payment.component";
import { PaymentMethodsComponent } from "../../../../shared/components/financial/payment-methods/payment-methods.component";
import { AssetService } from "../../../../shared/services/assets/asset.service";
import { CustomerService } from "../../../../shared/services/customers/customers.service.mjs";
import { InvoiceService } from "../../../../shared/services/financial/invoice.service.mjs";
import { PaymentService } from "../../../../shared/services/financial/payment.service.mjs";
import { SubscriptionService } from "../../../../shared/services/products/subscription.service.mjs";
import { AssetCardComponent } from "../../assets/asset-card/asset-card.component";
import { InvoiceCardComponents } from "../../financial/invoices/invoice-card/invoice-card.component";
import { PaymentCardComponents } from "../../financial/payments/payment-card/payment-card.component";
import { SubscriptionCardComponent } from "../../subscriptions/subscription-card/subscription-card.component";

@Component({
  selector: "app-customer-view",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SubscriptionCardComponent,
    ConfirmDialogModule,
    ToastModule,
    AssetCardComponent,
    InvoiceCardComponents,
    PaymentCardComponents,
    BlockUIModule,
    CardModule,
    JsonToTextPipe,
    ProgressSpinnerModule,
    MenuModule,
    ButtonModule,
    PaymentMethodsComponent,
    ToggleSwitchModule,
    LatestInvoicePaymentComponent,
  ],
  providers: [MessageService, ConfirmationService, DialogService],
  templateUrl: "./customer-view.component.html",
  styleUrl: "./customer-view.component.scss",
})
export class CustomerViewComponent extends CommonComponent implements OnInit {
  protected _customer_id = new BehaviorSubject<string>("");
  customer_id$: Observable<string> = this._customer_id.asObservable();

  customer = signal<ICustomerOverview | undefined>(undefined);

  subscriptions: ISubscription[] | undefined;
  assets: IAsset[] | undefined;
  invoices: IInvoice[] | undefined;
  payments: IPayments[] | undefined;
  loading = true;

  items: MenuItem[] = [
    {
      label: "Edit",
      icon: "pi pi-file-edit",
      command: () => this.edit(),
    }
  ];


  edit() {
    this.router.navigate(['/admin/customers/edit', this.customer()?.id]);
  }

    constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    protected customerService: CustomerService,
    private subscriptionService: SubscriptionService,
    private assetService: AssetService,
    protected invoiceService: InvoiceService,
    protected paymentService: PaymentService
  ) {
    super();
    this.isLoading(true);

    this.isLoading$.subscribe((state) => {
      this.loading = state;
    });
  }
  ngOnInit(): void {
    this.activatedRoute.params.subscribe(async (params) => {
      this.loading = true;

      let id = params["id"];
      console.log("Firing OnInit", id);

      this._customer_id.next(id);

      const promises: Promise<any>[] = [];

      const customerPromise = this.customerService
        .getOverview(id)
        .then((result) => {
          if (result.status == FUNCTION_RESULT_STATUS.SUCCESS) {
            this.customer.set(result.data!);
          }
          return Promise.resolve();
        });

      promises.push(customerPromise);

      const subscriptionPromise = this.subscriptionService
        .getByCustomer(id)
        .then((result) => {
          if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
            this.subscriptions = result.data;
          }
          return Promise.resolve();
        });

      promises.push(subscriptionPromise);

      const assetPromise = this.assetService
        .getByCustomer(id)
        .then((result) => {
          if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
            this.assets = result.data;
          }
          return Promise.resolve();
        });

      promises.push(assetPromise);

      const invoicePromise = this.invoiceService
        .getByCustomer(id)
        .then((result) => {
          if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
            this.invoices = result.data!;
          }
          return Promise.resolve();
        });

      promises.push(invoicePromise);

      const paymentPromise = this.paymentService
        .getByCustomer(id)
        .then((result) => {
          if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
            this.payments = result.data!;
          }
          return Promise.resolve();
        });

      promises.push(paymentPromise);

      Promise.all(promises).finally(() => {
        this.loading = false;
        this.isLoading(false);
      });
    });
  }
}
